import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ScissorsSquare, Package, CheckCircle2, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["pending", "scheduled", "in_progress", "delivered"];

const CATEGORY_LABEL = {
  womens: "Women's",
  mens: "Men's",
  kids: "Kids'",
  alteration: "Alteration",
  home_furnishing: "Home Furnishing",
};

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E5E0D8] p-6" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-label text-[#5C5A55]">{label}</div>
          <div className="font-display text-4xl mt-2">{value}</div>
        </div>
        <div className="w-12 h-12 border border-[#E5E0D8] flex items-center justify-center">
          <Icon size={20} className="text-[#6B1F2B]" />
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total_bookings: 0, pending_bookings: 0, delivered_bookings: 0, total_contacts: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, s] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/stats`),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#5C5A55] link-underline"
              data-testid="admin-back-link"
            >
              <ArrowLeft size={16} /> Back to site
            </Link>
            <div className="font-display text-2xl">
              Stich<span className="text-[#6B1F2B]">B</span> · Admin
            </div>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="text-sm border border-[#1A1A1A] px-4 py-2 hover:bg-[#1A1A1A] hover:text-white transition-colors"
          >
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Total Bookings" value={stats.total_bookings} icon={ScissorsSquare} />
          <StatCard label="Pending" value={stats.pending_bookings} icon={Package} />
          <StatCard label="Delivered" value={stats.delivered_bookings} icon={CheckCircle2} />
          <StatCard label="Messages" value={stats.total_contacts} icon={MessageSquare} />
        </div>

        <div className="flex gap-2 border-b border-[#E5E0D8] mb-8">
          {[
            { id: "bookings", label: `Bookings (${bookings.length})` },
            { id: "contacts", label: `Messages (${contacts.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-6 py-3 font-label transition-colors ${
                tab === t.id ? "border-b-2 border-[#6B1F2B] text-[#6B1F2B]" : "text-[#5C5A55]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#5C5A55]" data-testid="admin-loading">Loading…</div>
        ) : tab === "bookings" ? (
          <div className="bg-white border border-[#E5E0D8] overflow-x-auto" data-testid="admin-bookings-table">
            <table className="w-full text-sm">
              <thead className="bg-[#EFEBE3]">
                <tr className="text-left font-label text-[#5C5A55]">
                  <th className="p-4">Customer</th>
                  <th className="p-4">Service</th>
                  <th className="p-4">Pickup</th>
                  <th className="p-4">Address</th>
                  <th className="p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-12 text-center text-[#5C5A55]">
                      No bookings yet.
                    </td>
                  </tr>
                ) : (
                  bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#E5E0D8]" data-testid={`booking-row-${b.id}`}>
                      <td className="p-4">
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-[#5C5A55]">{b.phone}</div>
                        {b.email && <div className="text-xs text-[#5C5A55]">{b.email}</div>}
                      </td>
                      <td className="p-4">
                        <div>{CATEGORY_LABEL[b.service_category] || b.service_category}</div>
                        <div className="text-xs text-[#5C5A55]">{b.garment_type}</div>
                      </td>
                      <td className="p-4">
                        <div>{b.pickup_date}</div>
                        <div className="text-xs text-[#5C5A55]">{b.pickup_time_slot}</div>
                      </td>
                      <td className="p-4 max-w-xs">
                        <div className="text-xs text-[#5C5A55] truncate" title={b.pickup_address}>
                          {b.pickup_address}
                        </div>
                        {b.notes && <div className="text-xs text-[#5C5A55] mt-1 italic truncate" title={b.notes}>{b.notes}</div>}
                      </td>
                      <td className="p-4">
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className="w-40 rounded-none" data-testid={`status-select-${b.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s.replace("_", " ")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E0D8]" data-testid="admin-contacts-list">
            {contacts.length === 0 ? (
              <div className="p-12 text-center text-[#5C5A55]">No messages yet.</div>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="p-6 border-b border-[#E5E0D8] last:border-b-0" data-testid={`contact-row-${c.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-[#5C5A55]">
                        {c.email}
                        {c.phone && ` · ${c.phone}`}
                      </div>
                    </div>
                    <div className="text-xs text-[#5C5A55]">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-[#1A1A1A] mt-2">{c.message}</div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
